/**
 * 从 MP4/MOV（ISO BMFF）文件头读取元数据，避免整段视频解码：
 * - mvhd：时长（timescale / duration）
 * - moov/udta/meta/ilst/covr：部分文件内嵌封面（常见于手机导出、带封面的 MP4）
 *
 * 仅读取文件切片（头部 + 必要时尾部），不把整文件载入 JS 堆。
 */

const HEAD_READ_MAX = 16 * 1024 * 1024;
const TAIL_READ_MAX = 12 * 1024 * 1024;

function readU32BE(d: Uint8Array, o: number): number {
  return ((d[o] << 24) | (d[o + 1] << 16) | (d[o + 2] << 8) | d[o + 3]) >>> 0;
}

function readFourCC(d: Uint8Array, o: number): string {
  return String.fromCharCode(d[o], d[o + 1], d[o + 2], d[o + 3]);
}

function isJpeg(sig: Uint8Array): boolean {
  return sig.length >= 3 && sig[0] === 0xff && sig[1] === 0xd8 && sig[2] === 0xff;
}

function isPng(sig: Uint8Array): boolean {
  return (
    sig.length >= 8 &&
    sig[0] === 0x89 &&
    sig[1] === 0x50 &&
    sig[2] === 0x4e &&
    sig[3] === 0x47 &&
    sig[4] === 0x0d &&
    sig[5] === 0x0a &&
    sig[6] === 0x1a &&
    sig[7] === 0x0a
  );
}

interface BoxIter {
  type: string;
  /** payload start (after size+type[+largesize]) */
  p0: number;
  /** box end (exclusive) */
  p1: number;
}

function iterateBoxes(d: Uint8Array, start: number, end: number): BoxIter[] {
  const out: BoxIter[] = [];
  let o = start;
  while (o + 8 <= end) {
    let size = readU32BE(d, o);
    const type = readFourCC(d, o + 4);
    let header = 8;
    let boxEnd: number;
    if (size === 0) {
      boxEnd = end;
    } else if (size === 1) {
      if (o + 16 > end) break;
      const hi = readU32BE(d, o + 8);
      const lo = readU32BE(d, o + 12);
      const large = (BigInt(hi) << 32n) | BigInt(lo);
      header = 16;
      boxEnd = o + Number(large);
      if (boxEnd > end || boxEnd <= o + header) break;
    } else {
      boxEnd = o + size;
      if (boxEnd > end || boxEnd <= o + header) break;
    }
    out.push({ type, p0: o + header, p1: boxEnd });
    o = boxEnd;
  }
  return out;
}

/** 解析 mvhd 有效载荷（不含外层 8 字节 size+type） */
function parseMvhdDuration(payload: Uint8Array): number | null {
  if (payload.length < 8) return null;
  const version = payload[0];
  let timescale: number;
  if (version === 1) {
    if (payload.length < 32) return null;
    timescale = readU32BE(payload, 20);
    const dHi = readU32BE(payload, 24);
    const dLo = readU32BE(payload, 28);
    const durationRaw = (BigInt(dHi) << 32n) | BigInt(dLo >>> 0);
    if (timescale === 0) return null;
    return Number(durationRaw) / timescale;
  }
  if (version === 0) {
    if (payload.length < 20) return null;
    timescale = readU32BE(payload, 12);
    const durationRaw = readU32BE(payload, 16);
    if (timescale === 0) return null;
    return durationRaw / timescale;
  }
  return null;
}

/** 在 covr 原子内找 data 负载中的图片字节 */
function extractCovrImage(covrPayload: Uint8Array): Uint8Array | null {
  const boxes = iterateBoxes(covrPayload, 0, covrPayload.length);
  for (const b of boxes) {
    if (b.type !== "data") continue;
    const inner = covrPayload.subarray(b.p0, b.p1);
    if (inner.length < 8) continue;
    const afterHeader = inner.subarray(8);
    if (isJpeg(afterHeader) || isPng(afterHeader)) return afterHeader;
    if (isJpeg(inner) || isPng(inner)) return inner;
  }
  return null;
}

function walkForCoverAndDuration(d: Uint8Array, start: number, end: number, acc: { duration: number | null; cover: Uint8Array | null }): void {
  for (const b of iterateBoxes(d, start, end)) {
    if (b.type === "moov" || b.type === "trak" || b.type === "udta") {
      walkForCoverAndDuration(d, b.p0, b.p1, acc);
      continue;
    }
    if (b.type === "mvhd") {
      const pl = d.subarray(b.p0, b.p1);
      const sec = parseMvhdDuration(pl);
      if (sec != null && !Number.isNaN(sec) && sec > 0) acc.duration = sec;
      continue;
    }
    if (b.type === "meta") {
      const metaPl = d.subarray(b.p0, b.p1);
      if (metaPl.length < 4) continue;
      const children = iterateBoxes(metaPl, 4, metaPl.length);
      for (const ch of children) {
        if (ch.type !== "ilst") continue;
        const ilstPl = metaPl.subarray(ch.p0, ch.p1);
        for (const item of iterateBoxes(ilstPl, 0, ilstPl.length)) {
          if (item.type !== "covr") continue;
          const covrInner = ilstPl.subarray(item.p0, item.p1);
          const img = extractCovrImage(covrInner);
          if (img) {
            acc.cover = img;
            break;
          }
        }
        if (acc.cover) break;
      }
      continue;
    }
  }
}

function parseBuffer(d: Uint8Array): { duration: number | null; cover: Uint8Array | null } {
  const acc = { duration: null as number | null, cover: null as Uint8Array | null };
  walkForCoverAndDuration(d, 0, d.length, acc);
  return acc;
}

export function isProbablyMp4FamilyFile(file: File): boolean {
  const t = (file.type || "").toLowerCase();
  if (t.includes("mp4") || t.includes("quicktime") || t.includes("m4v")) return true;
  const n = file.name.toLowerCase();
  return /\.(mp4|m4v|mov)(\s|$)/i.test(n) || n.endsWith(".mp4") || n.endsWith(".m4v") || n.endsWith(".mov");
}

export interface Mp4FastParseResult {
  durationSec: number;
  coverBlob: Blob;
  coverMime: "image/jpeg" | "image/png";
}

/**
 * 若能在切片内同时得到 mvhd 时长与 covr 封面，则返回结果；否则返回 null（应回退 video 解码）
 */
export async function tryReadMp4EmbeddedMetadata(file: File): Promise<Mp4FastParseResult | null> {
  if (!isProbablyMp4FamilyFile(file) || file.size < 32) return null;

  const headLen = Math.min(file.size, HEAD_READ_MAX);
  const headBuf = new Uint8Array(await file.slice(0, headLen).arrayBuffer());
  let { duration, cover } = parseBuffer(headBuf);

  if ((duration == null || cover == null) && file.size > headLen) {
    const tailLen = Math.min(file.size, TAIL_READ_MAX);
    const tailBuf = new Uint8Array(await file.slice(file.size - tailLen, file.size).arrayBuffer());
    const t = parseBuffer(tailBuf);
    if (duration == null && t.duration != null) duration = t.duration;
    if (cover == null && t.cover != null) cover = t.cover;
  }

  if (duration == null || cover == null || duration <= 0) return null;

  const mime: "image/jpeg" | "image/png" = isJpeg(cover) ? "image/jpeg" : isPng(cover) ? "image/png" : "image/jpeg";
  if (!isJpeg(cover) && !isPng(cover)) return null;

  return {
    durationSec: parseFloat(duration.toFixed(3)),
    coverBlob: new Blob([cover], { type: mime }),
    coverMime: mime,
  };
}
